import React, { useEffect, useState } from 'react';
import { Accordion, Tab } from 'react-bootstrap';
import { useLocation, useParams } from 'react-router-dom';

import ApplicationForm from './ApplicationForm';
import AppCharts from './AppCharts';
import StatementInfoForm from './StatementInfoForm';
import InstantSettlementForm from './InstantSettlementForm';
import ThirdPartyApiKeys from './ThirdPartyApiKeys';
import ThirdPartyWebhooks from './ThirdPartyWebhooks';
import ConsoleKYCFlow from './ConsoleKYCFlow';
import {
  AppProfileNavItem,
  AppCardHeader,
  AppCardBody,
  AppProfileDetailColumn,
  AppProfileTabColumn,
} from './AppProfileViewComponents';
import { useAlerts } from '../context';
import { appsApi } from '../../api';
import FreezeModal from '../common/FreezeModal';
import { handleEnterKeyPress, hasPermissions } from '../../utils';

export default function AppPanels({
  app,
  t,
  onDelete,
  isMobile,
  onSave,
  user,
  onFreeze,
  shouldBeOpen = true,
  lastItem = null,
  isAppsView = false,
}) {
  const { action } = useParams();
  const [header, setHeader] = useState(action);
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const { setAlert } = useAlerts();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(shouldBeOpen);

  useEffect(() => {
    if (!location.pathname.includes('/apps')) setHeader(action);
  }, [action]);

  useEffect(() => {
    if (location.pathname.includes('/apps')) setHeader('detail');
  }, []);

  const handleSubmitFreezeApp = async () => {
    const desiredFreezeState = !app.frozen;
    const response = await appsApi.freezeApp(app.id, desiredFreezeState);
    if (response.success) {
      setAlert({
        message: t('applications.detail.form.messages.status', {
          name: app.name,
          status: (desiredFreezeState
            ? t('common:labels.frozen')
            : t('common:labels.unfrozen')
          ).toLowerCase(),
        }),
        variant: 'success',
      });
      onFreeze(app.id, { frozen: desiredFreezeState });
    } else if (!response.success) {
      if (response.status_code === 400) {
        setAlert({
          message: response.message,
          variant: 'danger',
        });
      } else if (response.status_code === 409) {
        setAlert({
          message: response.message,
          variant: 'warning',
        });
        onFreeze(app.id, { frozen: desiredFreezeState });
      }
    }
    setShowFreezeModal(false);
  };

  // values to be displayed in the FeezeModal
  const type = t('common:applications.singular');
  const { name } = app;

  const body = (
    <AppCardBody header={header} setHeader={setHeader}>
      <AppProfileTabColumn>
        <AppProfileNavItem
          type={header}
          appId={app.id}
          t={t}
          setHeader={setHeader}
          route="detail"
        />
        {user.role &&
          (user.role === 1 || user.role === 2) &&
          hasPermissions(user, 'apps', 'Write') && (
            <AppProfileNavItem
              type={header}
              appId={app.id}
              t={t}
              setHeader={setHeader}
              route="statement-info"
            />
          )}
        {hasPermissions(user, 'apps', 'Write') && (
          <AppProfileNavItem
            type={header}
            appId={app.id}
            t={t}
            setHeader={setHeader}
            route="access-keys"
          />
        )}
        {hasPermissions(user, 'apps', 'Write') && (
          <AppProfileNavItem
            type={header}
            appId={app.id}
            t={t}
            setHeader={setHeader}
            route="provider-webhooks"
          />
        )}
        <AppProfileNavItem
          type={header}
          appId={app.id}
          t={t}
          setHeader={setHeader}
          route="metrics"
        />
        {user.role &&
          (user.role === 1 || user.role === 2) &&
          hasPermissions(user, 'apps', 'Write') && (
            <AppProfileNavItem
              type={header}
              appId={app.id}
              t={t}
              route="instant-settlement"
              setHeader={setHeader}
            />
          )}
        {hasPermissions(user, 'kyc', 'Write') && (
          <AppProfileNavItem
            type={header}
            appId={app.id}
            t={t}
            route="kyc-flows"
            setHeader={setHeader}
          />
        )}
      </AppProfileTabColumn>

      <AppProfileDetailColumn>
        <Tab.Content className="w-100">
          <Tab.Pane eventKey="detail">
            {isOpen && (
              <ApplicationForm
                isEditing={false}
                onDelete={onDelete}
                initialValues={app}
                isMobile={isMobile}
                onSave={onSave}
                disabled
              />
            )}
          </Tab.Pane>
          <Tab.Pane eventKey="statement-info">
            {isOpen &&
              user.role &&
              (user.role === 1 || user.role === 2) &&
              hasPermissions(user, 'apps', 'Write') && (
                <StatementInfoForm
                  initialValues={app}
                  user={user}
                  isMobile={isMobile}
                  onSave={onSave}
                />
              )}
          </Tab.Pane>
          <Tab.Pane eventKey="access-keys">
            {isOpen && hasPermissions(user, 'apps', 'Write') && (
              <ThirdPartyApiKeys isMobile={isMobile} onSave={onSave} appId={app.id} />
            )}
          </Tab.Pane>
          <Tab.Pane eventKey="instant-settlement" className="px-4">
            {isOpen && hasPermissions(user, 'apps', 'Write') && (
              <InstantSettlementForm initialValues={app} onSave={onSave} />
            )}
          </Tab.Pane>
          <Tab.Pane eventKey="metrics" className="px-4">
            {isOpen && <AppCharts appId={app.id} />}
          </Tab.Pane>
          <Tab.Pane eventKey="provider-webhooks">
            {isOpen && hasPermissions(user, 'apps', 'Write') && (
              <ThirdPartyWebhooks appId={app.id} />
            )}
          </Tab.Pane>
          <Tab.Pane eventKey="kyc-flows" className="px-4">
            {isOpen && hasPermissions(user, 'kyc', 'Write') && <ConsoleKYCFlow app={app} t={t} />}
          </Tab.Pane>
        </Tab.Content>
      </AppProfileDetailColumn>
    </AppCardBody>
  );

  const toggle = (
    <div
      role="button"
      tabIndex={0}
      onClick={() => setIsOpen(!isOpen)}
      onKeyDown={() => handleEnterKeyPress(setIsOpen(!isOpen))}
      aria-label={isOpen ? 'Collapse section' : 'Expand section'}
    >
      <AppCardHeader setShowFreezeModal={setShowFreezeModal} app={app} t={t} user={user}>
        <div className="ms-auto d-flex align-items-center">
          <span
            className={`text-primary fas ${isOpen ? 'fa-chevron-down' : 'fa-chevron-left'} ps-2`}
          />
        </div>
      </AppCardHeader>
    </div>
  );

  return (
    <>
      {isAppsView ? (
        <Accordion activeKey={isOpen ? '0' : 'null'} ref={lastItem}>
          {toggle}
          <Accordion.Collapse eventKey="0">{body}</Accordion.Collapse>
        </Accordion>
      ) : (
        <>
          <AppCardHeader setShowFreezeModal={setShowFreezeModal} app={app} t={t} user={user} />
          {body}
        </>
      )}

      {showFreezeModal && (
        <FreezeModal
          show={showFreezeModal}
          data={{}}
          handleFreeze={handleSubmitFreezeApp}
          setShow={setShowFreezeModal}
          isAdmin={false}
          name={name}
          type={type}
        />
      )}
    </>
  );
}
